import { Alert, Button, Col, Divider, Row, Switch, Typography } from 'antd'
import { LeftOutlined as BackIcon, FullscreenOutlined, RightOutlined as NextIcon } from '@ant-design/icons'
import {
  DEFAULT_MAX_BOOKING_MINUTES,
  getBookingLimitMsg,
  getDisplayDate,
  getLatestAllowedBooking,
  isLastMinute,
} from '../../util/bookingUtil'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import _, { set } from 'lodash'
import { add, formatISO, isBefore, isWeekend, sub } from 'date-fns'
import { durationInWords, formatDateTime, parseDateTime } from '../../util/dateTimeUtil'
import { getServerDate, isToday } from '../../util/serverTimeUtil'
import { hideAllNotifications, notifyError, notifySuccess } from '../../util/notifyUtil'

import AppContext from '../common/AppContext'
import BookingDeleteConfirmationModal from './BookingDeleteConfirmationModal'
import BookingInstructionsModal from './BookingInstructionsModal'
import BookingMessageModal from './BookingMessageModal'
import BookingModal from './BookingModal'
import { CURRENT_YEAR } from '../../util/commonUtil'
import { CalendarOutlined } from '@ant-design/icons'
import CourtSchedule from './CourtSchedule'
import CustomDatePicker from '../common/CustomDatePicker'
import { EmailLink } from '../common/EmailLink'
import { InvalidTimezone } from '../error/InvalidTimezone'
import LoginLink from '../common/LoginLink'
import UpcomingBookings from './UpcomingBookings'
import axios from '../../axios/axios'
import { config } from '../../util/config'
import { transformServerError } from '../../util/errorUtil'
import { useMount } from 'ahooks'
import useResponsive from '../../hooks/useResponsive'

const { Text, Paragraph, Title } = Typography

const BookingPage = ({ numDays = 1 }) => {
  const [bookings, setBookings] = useState([])
  const [modalVisible, setModalVisible] = useState(false)
  const [refreshedAt, setRefreshedAt] = useState(getServerDate())
  const [deleteConfirmationModalVisible, setDeleteConfirmationModalVisible] = useState(false)
  const [selectedBookingToDelete, setSelectedBookingToDelete] = useState()
  const [messageModalVisible, setMessageModalVisible] = useState(false)
  const [modalMessage, setModalMessage] = useState()
  const [lastBooking, setLastBooking] = useState()
  const [nextAllowed, setNextAllowed] = useState()
  const [bookingTimeRemaining, setBookingTimeReamining] = useState()
  const [maxBookingTime, setMaxBookingTime] = useState()
  const [nextBookings, setNextBookings] = useState()
  const [kioskMode, setKioskMode] = useState(false)
  const [loading, setLoading] = useState()
  // const [bookings, setBookings] = useState()
  const [date, setDate] = useState(getServerDate())
  const [selectedTimeslot, setSelectedTimeslotStart] = useState()
  const [selectedDuration, setSelectedDuration] = useState()
  const [selectedBooking, setSelectedBooking] = useState()
  const [readonly, setReadonly] = useState(true)
  const [selectedCourt, setSelectedCourt] = useState()
  const bp = useResponsive()
  let { currentUser, validTimezone, serverTimeLoaded } = useContext(AppContext)
  const navigate = useNavigate()
  const location = useLocation()

  validTimezone = validTimezone || currentUser?.username === 'test' // Allow test user to bypass timezone check for testing purposes

  const [refreshKey, setRefreshKey] = useState(0)

  const onSelected = (booking) => {
    setDate(new Date(booking.startTime))
    handleEdit(booking)
  }

  useEffect(() => {
    if (serverTimeLoaded) {
      const serverNow = getServerDate()
      setDate(serverNow)
      setRefreshedAt(serverNow)
    }
  }, [serverTimeLoaded])

  const multiDay = numDays > 1
  const multiDaySmallScreen = multiDay && !bp.lg

  const forceRefresh = () => {
    setRefreshKey((prev) => prev + 1)
  }

  const getDayControl = (date) => {
    const className = isWeekend(date) ? 'bookingDayLinkWeekend' : 'bookingDayLink'
    return (
      <Button key={date} type="link" className={className} onClick={() => setDate(date)} style={{ paddingLeft: '0px' }}>
        {isToday(date) ? 'Today' : formatDateTime(date, bp.lg && !multiDay ? 'EEEE' : 'EEE')}
      </Button>
    )
  }

  const getControls = () => {
    const previousLabel = multiDay ? 'Previous day' : 'Previous'
    const nextLabel = multiDay ? 'Next day' : 'Next'

    const dayControls = []
    for (let i = 0; i <= 7; i++) {
      const day = add(getServerDate(), { days: i })
      dayControls.push(getDayControl(day))
    }
    return (
      <>
        <Row gutter={20} justify="space-between">
          <Col flex={1} style={{ textAlign: 'center' }}>
            {dayControls}
          </Col>
          <Col flex={1} style={{ textAlign: 'center' }}>
            {!multiDaySmallScreen && (
              <>
                <Button type="link" onClick={previousDay}>
                  <BackIcon /> {previousLabel}
                </Button>
                <Button type="link" onClick={nextDay} disabled={!canMoveToNextDay}>
                  {nextLabel} <NextIcon />
                </Button>
              </>
            )}
            {!multiDay && !bp.xsOrSm && (
              <Switch
                checked={multiDay}
                onChange={(checked) => navigate(checked ? '/week' : '/courtbooking')}
                checkedChildren="Week view"
                unCheckedChildren="Week view"
                style={{ marginLeft: '10px' }}
              />
            )}
          </Col>
          {multiDay && (
            <>
              <Col flex={1} style={{ textAlign: 'center' }}>
                <Button type="link" onClick={previousWeek}>
                  <BackIcon /> Previous week
                </Button>
                <Button type="link" onClick={nextWeek} disabled={!canMoveToNextWeek}>
                  Next week <NextIcon />
                </Button>
              </Col>
              {bp.xl && (
                <Col flex={1} style={{ textAlign: 'center' }}>
                  <Switch
                    checked={multiDay}
                    onChange={(checked) => navigate(checked ? '/week' : '/courtbooking')}
                    checkedChildren="Week view"
                    unCheckedChildren="Week view"
                    style={{ marginLeft: '3em' }}
                  />
                </Col>
              )}
            </>
          )}
        </Row>
      </>
    )
  }

  const getLatestBooking = async () => {
    if (!currentUser) {
      return
    }
    try {
      setLoading(true)
      const result = await axios.get('/my/lastBooking')
      const { last, nextAllowed, maxBookingTime = DEFAULT_MAX_BOOKING_MINUTES, bookingTimeRemaining } = result?.data
      setLastBooking(last)
      setNextAllowed(nextAllowed)
      setBookingTimeReamining(bookingTimeRemaining)
      setMaxBookingTime(maxBookingTime)
    } catch (e) {
      notifyError('error', e)
    } finally {
      setLoading(false)
    }
  }
  const getNextBooking = async () => {
    if (!currentUser) {
      return
    }
    try {
      const result = await axios.get('/my/nextBookings')
      setNextBookings(result?.data)
    } catch (e) {
      notifyError('error', e)
    }
  }

  const showMessage = (title, message) => {
    setModalMessage({ title, message })
    if (message) {
      setMessageModalVisible(true)
      hideAllNotifications()
    }
  }
  const closeMessage = () => {
    setModalMessage(null)
    setMessageModalVisible(false)
  }

  const previousDay = () => setDate((date) => sub(date, { days: 1 }))
  const nextDay = () => setDate((date) => add(date, { days: 1 }))
  const previousWeek = () => setDate((date) => sub(date, { weeks: 1 }))
  const nextWeek = () => setDate((date) => add(date, { weeks: 1 }))
  const latestAllowedBooking = getLatestAllowedBooking(currentUser)
  const canMoveToNextDay = isBefore(add(date, { days: 1 }), latestAllowedBooking)
  const canMoveToNextWeek = isBefore(add(date, { weeks: 1 }), latestAllowedBooking)

  const refresh = () => {
    getLatestBooking()
    getNextBooking()
    forceRefresh()
  }

  useEffect(() => {
    let isKioskMode = _.includes(location?.pathname, 'kiosk')
    if (isKioskMode) {
      setKioskMode(true)
      const interval = setInterval(
        () => {
          forceRefresh()
          setRefreshedAt(getServerDate())
        },
        60 * 60 * 1000,
      ) // Refresh every hour
      return () => clearInterval(interval)
    }
  }, [])

  useMount(() => {
    getLatestBooking()
    getNextBooking()
  })

  useEffect(() => {
    closeMessage()
    getLatestBooking()
    getNextBooking()
  }, [currentUser])

  const convertCourtNumbersToRefs = (courts) => _.map(courts, (id) => ({ id }))

  const handleSaveBooking = async (fields) => {
    const isNew = _.isNil(fields.id)
    // If the user can choose multiple courts for one booking, and they've selected
    // one or more courts, use those instead of the court they clicked on to start the booking.
    let courts
    if (isNew) {
      if (currentUser?.mayBookMultiCourt && fields.courts && _.size(fields.courts) > 0) {
        courts = convertCourtNumbersToRefs(fields.courts)
      } else {
        courts = [{ id: selectedCourt }]
      }
    } else {
      courts = fields.courts
    }

    if (_.every(courts, _.isInteger)) {
      courts = convertCourtNumbersToRefs(courts)
    }

    // Use the selected timeslot as the start time for a new booking, unless the new
    // booking has a parent (i.e. it's a repeating booking) in which case use the provided startTime.
    const { isRepeating } = fields
    const isRepeatingChildBooking = isNew && fields.parent
    const startTime = isNew && !isRepeatingChildBooking ? formatISO(selectedTimeslot) : fields.startTime
    if (!fields.endTime) {
      throw new Error('No duration was provided')
    }

    // A repeating booking will already have its end time calculated properly.
    const endTime = isRepeatingChildBooking
      ? fields.endTime
      : add(parseDateTime(startTime), { minutes: fields.endTime })

    const isLastMinuteBooking = isNew ? isLastMinute(startTime) : fields.isLastMinute || false
    let {
      player1IsGuest,
      player2IsGuest,
      player3IsGuest,
      player4IsGuest,
      guest1,
      guest2,
      guest3,
      guest4,
      player1,
      player2,
      player3,
      player4,
      ...rest
    } = fields

    // Clear out any prior player refs if they were changed to be guests.
    if (player1IsGuest || !player1?.id) player1 = null
    if (player2IsGuest || !player2?.id) player2 = null
    if (player3IsGuest || !player3?.id) player3 = null
    if (player4IsGuest || !player4?.id) player4 = null
    if (!!player1 || !guest1 || !player1IsGuest) {
      player1IsGuest = false
      guest1 = null
    }
    if (!!player2 || !guest2 || !player2IsGuest) {
      player2IsGuest = false
      guest2 = null
    }
    if (!!player3 || !guest3 || !player3IsGuest) {
      player3IsGuest = false
      guest3 = null
    }
    if (!!player4 || !guest4 || !player4IsGuest) {
      player4IsGuest = false
      guest4 = null
    }
    let booking = {
      ...rest,
      player1,
      player2,
      player3,
      player4,
      guest1,
      guest2,
      guest3,
      guest4,
      player1IsGuest,
      player2IsGuest,
      player3IsGuest,
      player4IsGuest,
      courts,
      startTime,
      endTime,
      isLastMinute: isLastMinuteBooking,
    }

    const url = isNew ? '/my/booking' : `/my/booking/${fields.id}`
    const save = isNew ? axios.post : axios.put

    let saveResult
    try {
      saveResult = await save(url, { data: booking })
      if (isNew) {
        if (!isRepeating) {
          notifySuccess(
            'success',
            'Booked successfully',
            <>
              You've successfully booked a court on {formatDateTime(startTime, 'EEEE MMMM d')} at{' '}
              {formatDateTime(startTime, 'h:mma')}.
            </>,
            10,
          )
        }
      } else {
        notifySuccess(
          'success',
          'Booking updated',

          <>Your booking changes have been saved.</>,
        )
      }
      setModalVisible(false)
      refresh()
    } catch (e) {
      const msg = transformServerError(e)
      if (msg.code === 'EXISTING_BOOKING') {
        notifyError('error', 'Court not available', 'Sorry, someone else has already booked that court.')
        console.log('Existing bookings', e.response?.data?.error?.details?.existingBookings)
        // setModalVisible(false)
        refresh()
      } else {
        notifyError('error', e)
      }
    }

    return saveResult.data
  }
  const handleCancel = useCallback(() => {
    setModalVisible(false)
  }, [])

  const handleAdd = useCallback(
    (time, court, duration, bookings) => {
      if (!duration) {
        throw new Error('No court booking duration was provided')
      }
      if (!validTimezone) {
        return
      }
      const isLastMinuteBooking = isLastMinute(time)
      if (!currentUser) {
        showMessage(
          "You're not logged in",
          <>
            <Paragraph>
              <LoginLink>Log in now</LoginLink> in to book a court.
            </Paragraph>
            <Paragraph>
              Or, <Link to="/register">register with the club</Link> if you're not yet a {CURRENT_YEAR} club member.
            </Paragraph>
          </>,
        )
        return
      }
      if (currentUser.mayBookCourts === false) {
        showMessage(
          "You don't have permission to book courts.",
          <>
            <Paragraph>You don't have permission to book courts right now.</Paragraph>
            <Paragraph>
              Please contact <EmailLink email="webadmin" /> if you think this is an error.
            </Paragraph>
          </>,
        )
        return
      } else if (nextAllowed && !currentUser.mayIgnoreWaitPeriod && !isLastMinuteBooking) {
        showMessage(
          "You can't book this court yet.",
          <>
            <Paragraph>
              <b>{getBookingLimitMsg(maxBookingTime)}</b>. You'll be able to book another court in{' '}
              {durationInWords(nextAllowed, false)} (at {formatDateTime(nextAllowed, 'h:mm a')})
            </Paragraph>
            <Paragraph>
              You <i>are</i> allowed to book any court that's available within the next 2 hours, however. This is a{' '}
              <i>last-minute</i> booking and doesn't count towards your 22-hour booking window.
            </Paragraph>
          </>,
        )
        return
      }
      setSelectedTimeslotStart(time)
      setSelectedDuration(duration)
      setSelectedBooking(null)
      setBookings(bookings)
      setSelectedCourt(court)
      setModalVisible(true)
      hideAllNotifications()
    },
    [currentUser, nextAllowed, maxBookingTime, validTimezone],
  )

  const handleEdit = useCallback(
    (booking, readonly = true) => {
      if (!validTimezone) {
        return
      }
      setSelectedBooking(booking)
      setReadonly(readonly)
      setSelectedCourt(null)
      setSelectedTimeslotStart(null)
      setSelectedDuration(null)
      setModalVisible(true)
      hideAllNotifications()
    },
    [validTimezone],
  )

  const handleOpenDifferentBooking = useCallback(
    (booking) => {
      setModalVisible(false)
      handleEdit(booking)
    },
    [handleEdit],
  )

  const handleDelete = useCallback(
    (booking) => {
      if (!validTimezone) {
        return
      }
      if (booking && booking.id) {
        axios.delete(`/my/booking/${booking.id}`, {}).then(
          (result) => {
            notifySuccess('success', 'Booking cancelled', <>You've cancelled this court booking.</>, 4)
            setModalVisible(false)
            setDeleteConfirmationModalVisible(false)
            setSelectedBookingToDelete(null)
            refresh()
          },
          (e) => {
            notifyError('error', e)
          },
        )
      } else {
        notifyError('error', 'There was a problem deleting your court booking.')
      }
    },
    [validTimezone, refresh],
  )

  const handleDeleteRecurring = (booking) => {
    if (!validTimezone) {
      return
    }
    if (booking && booking.id) {
      axios.delete(`/my/booking/recurring/${booking.id}`, {}).then(
        (result) => {
          notifySuccess(
            'success',
            'All repeating bookings cancelled',
            <>You've cancelled this court booking and all related repeating bookings.</>,
            4,
          )
          setModalVisible(false)
          setDeleteConfirmationModalVisible(false)
          setSelectedBookingToDelete(null)
          refresh()
        },
        (e) => {
          notifyError('error', e)
        },
      )
    } else {
      notifyError('error', 'There was a problem deleting your court booking.')
    }
  }

  const showDeleteConfirmation = useCallback((booking) => {
    if (booking) {
      setSelectedBookingToDelete(booking)
      setDeleteConfirmationModalVisible(true)
      hideAllNotifications()
    }
  }, [])
  if (!config.BOOKINGS_ENABLED) {
    return (
      <>
        <Title level={1}>Court Bookings </Title>
        {/* <Paragraph>
          Our courts are now closed and bookings have been disabled until the club opens again in the spring.
        </Paragraph> */}
        <Paragraph>
          Due to the bad weather the nets aren't up yet. Court bookings will open once the nets go up.
        </Paragraph>
      </>
    )
  }

  if (!validTimezone && currentUser?.username !== 'test') {
    return (
      <>
        <Title level={1}>Court Bookings </Title>
        <InvalidTimezone />
      </>
    )
  }

  let datePicker = null
  if (currentUser?.mayIgnoreFutureBookingLimit) {
    datePicker = (
      <>
        <CustomDatePicker
          suffixIcon={<CalendarOutlined />}
          style={{
            width: '32px',
            padding: 0,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
          }}
          inputReadOnly
          placeholder=""
          className="icon-only-picker"
          clearIcon={false}
          onChange={setDate}
        />
      </>
    )
  }

  return (
    <>
      {!kioskMode && (
        <>
          <BookingDeleteConfirmationModal
            booking={selectedBookingToDelete}
            visible={deleteConfirmationModalVisible}
            onOk={handleDelete}
            onDeleteRecurring={handleDeleteRecurring}
            onClose={() => setDeleteConfirmationModalVisible(false)}
          />
          <BookingMessageModal
            title={modalMessage?.title}
            message={modalMessage?.message}
            visible={messageModalVisible}
            onOk={closeMessage}
            onClose={closeMessage}
            style={{ top: 0 }}
          />
          <BookingModal
            visible={modalVisible}
            onOk={handleSaveBooking}
            onCancel={handleCancel}
            onDelete={() => showDeleteConfirmation(selectedBooking)}
            onOpenDifferentBooking={handleOpenDifferentBooking}
            court={selectedCourt}
            bookingTime={selectedTimeslot}
            bookingDuration={selectedDuration}
            bookingToEdit={selectedBooking}
            readonly={readonly}
            maxBookingTimeMinutes={maxBookingTime}
            bookingTimeRemaining={bookingTimeRemaining}
            bookings={bookings}
          />
          {!multiDay && (
            <>
              {/* <Title level={1}>Court Bookings </Title> */}
              <BookingInstructionsModal />
              {/* <Row justify="space-around">
                <Alert
                  style={{ maxWidth: '60em' }}
                  type="warning"
                  message="There's been a change to how you interact with your bookings"
                  description={
                    <>
                      The popup action menu on your personal bookings has been removed. Now, click or tap on your
                      bookings to edit or delete them. <br />
                      You can now also click or tap on any other booking to view its full details.
                    </>
                  }
                />
              </Row> */}
            </>
          )}
          {currentUser && !multiDay && (
            <Row justify="start" style={{ width: '100%', marginBottom: '0em' }}>
              <UpcomingBookings
                bookings={nextBookings}
                onSelected={onSelected}
                nextAllowed={nextAllowed}
                lastBooking={lastBooking}
                maxBookingTime={maxBookingTime}
              />
            </Row>
          )}
          <Row
            justify="space-around"
            className={multiDay ? 'bookingHeadingMultiDay' : 'bookingHeading'}
            // style={{ zIndex: 3, position: 'sticky', top: bp.xs ? 40 : 33, backgroundColor: '#fff' }}
            style={{ top: bp.xs ? 40 : 33 }}
          >
            {!multiDay && (
              <Col span={24} className="bookingDateHeading">
                <Text style={{ fontSize: '1.5em' }}>
                  {getDisplayDate(date)} {datePicker}
                </Text>
              </Col>
            )}
            <Col span={bp.xs ? 24 : null}>{!kioskMode && getControls()}</Col>
          </Row>
        </>
      )}

      {/* {kioskMode && (
        <Row justify="start">
          <Col>
            <h3>{formatDateTime(date, 'EEEE MMMM d')}</h3>
          </Col>
        </Row>
      )} */}

      {validTimezone && (
        <>
          <Row
            wrap={false}
            className={multiDay ? 'multiDayWrapper' : ''}
            style={{ width: numDays === 1 ? '100%' : 500 * numDays + 'px' }}
          >
            {_.range(0, numDays).map((index) => (
              <Col
                flex={1}
                style={{
                  minWidth: 0, // Prevents content from pushing width out
                  width: 0, // Forces the flex-basis to be zero so the day columns grow equally
                  borderLeft: multiDay ? '4px dotted #a5a5a58f' : '',
                }}
              >
                <CourtSchedule
                  date={add(date, { days: index })}
                  onAdd={handleAdd}
                  onEdit={handleEdit}
                  onDelete={showDeleteConfirmation}
                  bookingTimeRemaining={bookingTimeRemaining}
                  maxBookingTime={maxBookingTime}
                  forceRefresh={refreshKey}
                  isFirst={index === 0}
                  isLast={index === numDays - 1}
                  multiDay={multiDay}
                />
              </Col>
            ))}
          </Row>
          {kioskMode ? (
            <Link to="/courtbooking" style={{ float: 'right', paddingTop: '2em' }}>
              <FullscreenOutlined title="Normal mode" />
            </Link>
          ) : (
            <Link to="/courtbookingkiosk" style={{ float: 'right', paddingTop: '2em' }}>
              <FullscreenOutlined title="Fullscreen mode" />
            </Link>
          )}
        </>
      )}
    </>
  )
}

export default BookingPage
